import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MarketRecord, TemplateRecord } from "../types/index";
import { getMarkets } from "./useMarkets";
import {
  getAllTemplates,
  getTemplateById,
  getTemplates,
  getTemplatesTotals,
} from "./useTemplates";

const EMPTY_TEMPLATES: TemplateRecord[] = [];
const TEMPLATE_PAGE_SIZE = 200;
const MAX_TEMPLATE_FETCH_CONCURRENCY = 6;

async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number,
): Promise<T[]> {
  const results = new Array<T>(tasks.length);
  let cursor = 0;

  const worker = async () => {
    while (true) {
      const index = cursor++;
      if (index >= tasks.length) return;
      results[index] = await tasks[index]();
    }
  };

  const workerCount = Math.min(Math.max(1, concurrency), tasks.length);
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}

function byNameAndId(left: TemplateRecord, right: TemplateRecord) {
  const byName = String(left?.name || "").localeCompare(
    String(right?.name || ""),
    undefined,
    { sensitivity: "base" },
  );
  return (
    byName || String(left?.id ?? "").localeCompare(String(right?.id ?? ""))
  );
}

function mergeById(...sources: TemplateRecord[][]) {
  const seen = new Set<string>();
  const merged: TemplateRecord[] = [];
  for (const source of sources) {
    for (const template of source.slice().sort(byNameAndId)) {
      const id = String(template?.id ?? "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(template);
    }
  }
  return merged;
}

function getMarketIds(markets: MarketRecord[]) {
  return markets.map((market) => market?.id).filter((id) => id != null);
}

function eligiblePublicTemplates(
  results: TemplateRecord[][],
  markets: MarketRecord[],
) {
  const organizationMarketIds = new Set(getMarketIds(markets).map(String));
  return mergeById(
    results
      .flatMap((result) => result || [])
      .filter((template) =>
        Boolean(
          template?.isPublic &&
          template?.active !== false &&
          (template?.marketIds || []).some((id) =>
            organizationMarketIds.has(String(id)),
          ),
        ),
      ),
  );
}

async function fetchPublicTemplatePage(
  organizationId: number | string,
  markets: MarketRecord[],
  page: number,
  name?: string,
) {
  const marketIds = getMarketIds(markets);
  if (!marketIds.length) return { templates: [], hasMore: false };

  const results = await runWithConcurrency(
    marketIds.map((marketId) => async () => {
      try {
        return await getAllTemplates({
          organizationId,
          page,
          pageSize: TEMPLATE_PAGE_SIZE,
          marketId,
          name: name || null,
          active: true,
          markets,
        });
      } catch {
        return [];
      }
    }),
    MAX_TEMPLATE_FETCH_CONCURRENCY,
  );
  return {
    templates: eligiblePublicTemplates(results, markets),
    hasMore: results.some((result) => result.length === TEMPLATE_PAGE_SIZE),
  };
}

export async function fetchComplianceTemplates(
  organizationId: number | string | null | undefined,
): Promise<TemplateRecord[]> {
  if (!organizationId) return [];
  const markets = await getMarkets(organizationId, 0, TEMPLATE_PAGE_SIZE).catch(
    () => [],
  );
  return (await fetchPublicTemplatePage(organizationId, markets, 0)).templates;
}

export function useComplianceTemplates(
  organizationId: number | string | null | undefined,
) {
  const [data, setData] = useState<TemplateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(organizationId));

  useEffect(() => {
    if (!organizationId) {
      setData([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setData([]);
    setIsLoading(true);
    fetchComplianceTemplates(organizationId)
      .then((templates) => {
        if (!cancelled) setData(templates);
      })
      .catch((error) => {
        console.error(
          "[useComplianceTemplates] Failed to load templates:",
          error,
        );
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return { data, isLoading };
}

export default function useAvailablePrintTemplates({
  organizationId,
  apiTemplates = EMPTY_TEMPLATES,
  search = "",
  selectedTemplate,
  selectedTemplateId,
}: {
  organizationId: number | string | null | undefined;
  apiTemplates?: TemplateRecord[];
  search?: string;
  selectedTemplate?: TemplateRecord | null;
  selectedTemplateId?: number | string | null;
}) {
  const { data: complianceTemplates, isLoading: isLoadingCompliance } =
    useComplianceTemplates(organizationId);
  const [privateTemplates, setPrivateTemplates] = useState<TemplateRecord[]>(
    [],
  );
  const [searchPrivate, setSearchPrivate] = useState<TemplateRecord[]>([]);
  const [searchPublic, setSearchPublic] = useState<TemplateRecord[]>([]);
  const [retainedTemplate, setRetainedTemplate] =
    useState<TemplateRecord | null>(null);
  const [hydratedTemplate, setHydratedTemplate] =
    useState<TemplateRecord | null>(null);
  const [markets, setMarkets] = useState<MarketRecord[]>([]);
  const [areMarketsLoaded, setAreMarketsLoaded] = useState(false);
  const [isLoadingPrivate, setIsLoadingPrivate] = useState(
    Boolean(organizationId && !apiTemplates.length),
  );
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchPage, setSearchPage] = useState(0);
  const searchRequestRef = useRef(0);
  const loadMorePendingRef = useRef(false);
  const normalizedSearch = search.trim();

  useEffect(() => {
    let cancelled = false;
    setMarkets([]);
    setAreMarketsLoaded(false);
    setRetainedTemplate(null);
    setHydratedTemplate(null);
    setSearchPrivate([]);
    setSearchPublic([]);
    setSearchPage(0);
    setHasMore(false);
    if (!organizationId) {
      setAreMarketsLoaded(true);
      return;
    }
    getMarkets(organizationId, 0, TEMPLATE_PAGE_SIZE)
      .then((result) => {
        if (!cancelled) setMarkets(result || []);
      })
      .catch(() => {
        if (!cancelled) setMarkets([]);
      })
      .finally(() => {
        if (!cancelled) setAreMarketsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    if (apiTemplates.length || !organizationId) {
      setPrivateTemplates([]);
      setIsLoadingPrivate(false);
      return;
    }
    let cancelled = false;
    setPrivateTemplates([]);
    setIsLoadingPrivate(true);
    getTemplates({
      organizationId,
      page: 0,
      pageSize: TEMPLATE_PAGE_SIZE,
      active: true,
      markets: [],
    })
      .then((templates) => {
        if (!cancelled) setPrivateTemplates(templates || []);
      })
      .catch((error) => {
        console.error(
          "[useAvailablePrintTemplates] Failed to load private templates:",
          error,
        );
        if (!cancelled) setPrivateTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPrivate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiTemplates.length, organizationId]);

  useEffect(() => {
    searchRequestRef.current += 1;
    loadMorePendingRef.current = false;
    setSearchPrivate([]);
    setSearchPublic([]);
    setSearchPage(0);
    setHasMore(false);
    setIsSearching(Boolean(organizationId && normalizedSearch));
  }, [normalizedSearch, organizationId]);

  useEffect(() => {
    if (!organizationId || !normalizedSearch) {
      loadMorePendingRef.current = false;
      setIsSearching(false);
      return;
    }
    const requestId = ++searchRequestRef.current;
    let cancelled = false;
    setIsSearching(true);
    Promise.all([
      getTemplatesTotals({
        organizationId,
        name: normalizedSearch,
        active: true,
        page: searchPage,
        pageSize: TEMPLATE_PAGE_SIZE,
        sortField: "name",
        sortAscending: true,
        markets,
      }),
      fetchPublicTemplatePage(
        organizationId,
        markets,
        searchPage,
        normalizedSearch,
      ),
    ])
      .then(([privateResult, publicResult]) => {
        if (cancelled || requestId !== searchRequestRef.current) return;
        setSearchPrivate((current) =>
          searchPage === 0
            ? privateResult.data
            : mergeById(current, privateResult.data),
        );
        setSearchPublic((current) =>
          searchPage === 0
            ? publicResult.templates
            : mergeById(current, publicResult.templates),
        );
        setHasMore(
          (searchPage + 1) * TEMPLATE_PAGE_SIZE < privateResult.total ||
            publicResult.hasMore,
        );
      })
      .catch((error) => {
        if (cancelled || requestId !== searchRequestRef.current) return;
        console.error(
          "[useAvailablePrintTemplates] Failed to search templates:",
          error,
        );
        if (searchPage === 0) {
          setSearchPrivate([]);
          setSearchPublic([]);
        }
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled && requestId === searchRequestRef.current) {
          loadMorePendingRef.current = false;
          setIsSearching(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [markets, normalizedSearch, organizationId, searchPage]);

  const knownTemplates = useMemo(
    () =>
      mergeById(
        apiTemplates,
        privateTemplates,
        complianceTemplates,
        searchPrivate,
        searchPublic,
      ),
    [
      apiTemplates,
      complianceTemplates,
      privateTemplates,
      searchPrivate,
      searchPublic,
    ],
  );

  // Option-list identity changes must not cancel an unrelated in-flight by-ID
  // lookup. Depend on the selected record, not every regenerated option array.
  const knownSelectedTemplate = useMemo(
    () => knownTemplates.find(
      (template) => String(template?.id) === String(selectedTemplateId),
    ),
    [knownTemplates, selectedTemplateId],
  );

  useEffect(() => {
    if (selectedTemplate) {
      const selectedOrganizationId = selectedTemplate.organizationId;
      const belongsToOrganization = selectedTemplate.isPublic
        ? (!areMarketsLoaded || (selectedTemplate.marketIds || []).some((id) =>
            getMarketIds(markets).some((marketId) => String(marketId) === String(id))))
        : (selectedOrganizationId != null && Boolean(organizationId) &&
            String(selectedOrganizationId) === String(organizationId));
      setRetainedTemplate(belongsToOrganization ? selectedTemplate : null);
      setHydratedTemplate(null);
      return;
    }

    if (selectedTemplateId == null) {
      setRetainedTemplate(null);
      setHydratedTemplate(null);
      return;
    }

    if (knownSelectedTemplate) {
      setRetainedTemplate((current) =>
        String(current?.id ?? "") === String(knownSelectedTemplate.id)
          ? current
          : knownSelectedTemplate,
      );
      setHydratedTemplate(null);
      return;
    }

    if (
      !organizationId ||
      !areMarketsLoaded ||
      String(hydratedTemplate?.id ?? "") === String(selectedTemplateId)
    ) {
      return;
    }
    let cancelled = false;
    const currentOrganizationId = String(organizationId);
    setRetainedTemplate(null);
    setHydratedTemplate(null);
    getTemplateById({ organizationId, templateId: selectedTemplateId, markets })
      .then((template) => {
        if (cancelled || !template || template.active === false) return;
        const eligible = template.isPublic
          ? (template.marketIds || []).some((id) =>
              getMarketIds(markets).some(
                (marketId) => String(marketId) === String(id),
              ),
            )
          : String(template.organizationId ?? "") === currentOrganizationId;
        if (eligible) setHydratedTemplate(template);
      })
      .catch(() => {
        if (!cancelled) setHydratedTemplate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    areMarketsLoaded,
    hydratedTemplate?.id,
    knownSelectedTemplate,
    markets,
    organizationId,
    selectedTemplate,
    selectedTemplateId,
  ]);

  const data = useMemo(
    () =>
      mergeById(
        apiTemplates.length ? apiTemplates : privateTemplates,
        searchPrivate,
        complianceTemplates,
        searchPublic,
        retainedTemplate ? [retainedTemplate] : [],
        hydratedTemplate ? [hydratedTemplate] : [],
      ),
    [
      apiTemplates,
      complianceTemplates,
      hydratedTemplate,
      privateTemplates,
      retainedTemplate,
      searchPrivate,
      searchPublic,
    ],
  );

  const loadMore = useCallback(() => {
    if (!hasMore || isSearching || loadMorePendingRef.current) return;
    loadMorePendingRef.current = true;
    setIsSearching(true);
    setSearchPage((page) => page + 1);
  }, [hasMore, isSearching]);

  return {
    data,
    hasMore,
    isLoading: isLoadingCompliance || isLoadingPrivate,
    isSearching,
    loadMore,
  };
}
